/**
 * PURPOSE: Selector múltiple discreto de instituciones para la vista tabla de Programación (FTR-0040).
 * RESPONSIBILITIES: Elegir una, varias o todas las instituciones participantes y limpiar la selección, con
 *   casilla por opción, logo, búsqueda sin acentos y un valor compacto ("PUCP" / "3 instituciones").
 *   Solo entrega ids de institución; no filtra ni carga partidos.
 * COLLABORATORS: ProgramacionArea.jsx, programacionInstitutionFilter.js, react-select (convenciones de
 *   VenueSelect: portal a body, z-index 1800, menú acotado).
 * ROUTE: winscore/src/area/ProgramacionArea/InstitutionMultiSelect.jsx
 */
import React, { useMemo } from "react";
import Select, { components } from "react-select";

import { matchesInstitutionSearch } from "../../utils/programacionInstitutionFilter.js";

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <span className="flex min-w-0 items-center gap-2">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="h-4 w-4 flex-none rounded border-slate-300 text-indigo-700"
      />
      {props.data.logo ? (
        <img src={props.data.logo} alt="" className="h-5 w-5 flex-none object-contain" />
      ) : null}
      <span className="truncate text-sm text-slate-800">{props.data.label}</span>
    </span>
  </components.Option>
);

// Valor compacto: un nombre o "N instituciones", nunca una fila de chips.
const SummaryValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  if (!selected.length) return <components.ValueContainer {...props}>{children}</components.ValueContainer>;

  const items = React.Children.toArray(children);
  const input = items[items.length - 1];
  const label = selected.length === 1 ? selected[0].label : `${selected.length} instituciones`;

  return (
    <components.ValueContainer {...props}>
      <span className="truncate text-sm font-semibold text-slate-700" title={selected.map((o) => o.label).join(", ")}>
        {label}
      </span>
      {input}
    </components.ValueContainer>
  );
};

const MenuListWithActions = (props) => {
  const { onSelectAllInstitutions, onClearInstitutions } = props.selectProps;
  const keepFocus = (event) => event.preventDefault();

  return (
    <components.MenuList {...props}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5">
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={onSelectAllInstitutions}
          className="text-xs font-bold text-indigo-700 hover:underline"
        >
          Seleccionar todas
        </button>
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={onClearInstitutions}
          className="text-xs font-bold text-slate-600 hover:underline"
        >
          Limpiar
        </button>
      </div>
      {props.children}
    </components.MenuList>
  );
};

const InstitutionMultiSelect = ({
  options = [],
  value = [],
  onChange,
  onMenuOpen,
  disabled = false,
  isLoading = false,
  inputId = "programacion-institution-filter",
}) => {
  const selectedOptions = useMemo(() => {
    const ids = new Set(value.map(Number));
    return options.filter((option) => ids.has(Number(option.value)));
  }, [options, value]);

  const portalTarget = typeof document === "undefined" ? undefined : document.body;

  return (
    <div className="w-full min-w-0 sm:w-72">
      <Select
        inputId={inputId}
        aria-label="Filtrar por instituciones"
        isMulti
        options={options}
        value={selectedOptions}
        onChange={(next) => onChange?.((next || []).map((option) => Number(option.value)))}
        onMenuOpen={onMenuOpen}
        onSelectAllInstitutions={() => onChange?.(options.map((option) => Number(option.value)))}
        onClearInstitutions={() => onChange?.([])}
        isDisabled={disabled}
        isLoading={isLoading}
        isClearable
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        placeholder="Todas las instituciones"
        noOptionsMessage={() => "No hay instituciones"}
        loadingMessage={() => "Cargando instituciones…"}
        filterOption={({ data }, inputValue) => matchesInstitutionSearch(data, inputValue)}
        components={{
          Option: CheckboxOption,
          ValueContainer: SummaryValueContainer,
          MenuList: MenuListWithActions,
        }}
        menuPortalTarget={portalTarget}
        menuPosition={portalTarget ? "fixed" : "absolute"}
        menuPlacement="auto"
        maxMenuHeight={280}
        menuShouldScrollIntoView={false}
        captureMenuScroll
        classNamePrefix="institution-filter"
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: 38,
            borderRadius: 10,
            borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
            boxShadow: state.isFocused ? "0 0 0 2px #dbeafe" : "none",
            "&:hover": { borderColor: "#3b82f6" },
          }),
          valueContainer: (base) => ({ ...base, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }),
          menu: (base) => ({ ...base, minWidth: 260, maxWidth: "min(92vw, 420px)" }),
          menuPortal: (base) => ({ ...base, zIndex: 1800 }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? "#eef2ff" : "white",
            color: "#1e293b",
            cursor: "pointer",
          }),
        }}
      />
    </div>
  );
};

export default InstitutionMultiSelect;
